/**
 * 语文听写接口 —— **读侧 + 取词**（家长端与管理后台共用）。
 * 路径与语义见 `docs/接口契约对齐.md` 的「语文听写」章节。
 *
 * ## 2026-10-16：听写接口分成两类，本文件只装**读**
 *
 * 字表（课本 / 课文 / 汉字 / 词语 / 收录关系）是**站点资产**：
 * 维护归系统管理员（`dictationAdmin.ts` → `/admin/dictation/*`），
 * 使用归家长（`/students/:id/dictation/*`，孩子只是**权限锚点**）。
 *
 * 于是本文件里的每个函数都接一个 `DictationScope`，由它决定打到哪条路径：
 * 同一个 handler 在两个后端路由模块里是同一份实现（见
 * `apps/api/src/modules/dictation/dictation-reads.ts`），前端这边同理 ——
 * **一份解码、一份口径**，不因为"谁在看"而分叉。
 *
 * 写侧（新建/修改/删除/导入/拼音覆盖）**全部**在 `dictationAdmin.ts`。
 * ⛔ 家长端页面**不要** import 它 —— 那不是"藏起来"，是那些端点对家长根本不存在。
 *
 * | 方法 | 路径（`scope` 决定前缀） | 说明 |
 * | --- | --- | --- |
 * | GET   | `/dictation/textbooks` | 课本版本列表（含各自计数与各册收录数） |
 * | GET   | `/dictation/lessons` | 课文列表（`{lessons, total}`） |
 * | GET   | `/dictation/hanzi` | 生字列表（`{hanzi, total}`） |
 * | GET   | `/dictation/hanzi/:hanziId/words` | 这个字能组哪些词 |
 * | GET   | `/dictation/hanzi/:hanziId/readings` | 这个字的读音汇总（多音字按音造词） |
 * | GET   | `/dictation/words` | 词语列表（`{words, total}`） |
 * | GET   | `/dictation/textbooks/:textbookId/lists` | 这本书按「册 + 表」的收录数 |
 * | GET   | `/dictation/lists/items` | 某一册某一张表的内容 |
 * | POST  | `/dictation/words/pick` | 听写取词（**只读预览**；只对家长可用） |
 *
 * ## 这一层的铁律：**显式解码，绝不静默兜底**
 *
 * 本项目已经**三次**栽在同一件事上：字段名对不上 → 前端兜底成空数组 / 0 →
 * 界面看起来完全正常，只是那块数据永远不出现（孩子卡片 0 积分、统计页恒为 0、
 * 共享家长不显示名字）。
 *
 * 所以这里：
 *   1. ⛔ **不用 `pickArray()`**（`lib/api/shape.ts` 那个"找不到就返回空数组"的工具）。
 *      听写接口的字段名是**逐字确认过**的（`{textbooks}` / `{lessons,total}` /
 *      `{hanzi,total}` / `{words,total}`），`pickArray` 在这里只会把
 *      "后端把 `hanzi` 改名了"变成"你还没有生字"。
 *   2. 列表键**必须**是数组，否则整份数据记作 **`null`（拿不到）** ——
 *      界面显示「—」与一句"数据没拿到"，而不是「0 条」。
 *   3. 单条记录里的 `gradeCode` / `semester` / `pinyin` 等**可为 null**
 *      （后端契约如此），`null` 与"空串"在界面上都要能看出来。
 *   4. `total` 用 `toOptionalNumber`：真 0 就是 0，拿不到是 `null`。
 *
 * ⚠️ 分页列表在本项目其它模块里统一叫 `entries`，**听写模块不是** ——
 *   它用的是领域名，而且**每个端点的键都不一样**（都是从生产响应逐字抄下来的，
 *   夹具见 `packages/shared/src/__fixtures__/real-dictation-responses-2026-10-14.ts`）：
 *
 *   | 端点 | 真实响应键 | 数量字段 |
 *   | --- | --- | --- |
 *   | textbooks | `{ textbooks: [...] }` | 没有（每个课本自己带 `lessonCount/hanziCount/wordCount`） |
 *   | lessons | `{ lessons: [...], total }` | `total` |
 *   | hanzi | `{ hanzi: [...], total }` | `total` |
 *   | words | `{ words: [...], total }` | `total` |
 *   | words/pick | `{ applied, words, total }` | `total` |
 *   | hanzi/:id/words | `{ hanzi, words }` | 没有 |
 *
 *   ⛔ 不要"顺手改成 entries"，也不要把这几个键统一成一个名字 ——
 *   两者都会让界面变成一个**永远空着**的列表（这正是本文件开头那条铁律的由来）。
 *
 * ## 两个必须知道的真实行为（生产实测，2026-10-14）
 *
 * 1. `source: 'wrong'` → **501** + `{statusCode, code:'DICTATION_MISTAKES_NOT_READY', message}`
 *    （Fastify 默认错误体，不是应用的 `{ok:false,error}` 信封）。界面必须给人话。
 * 2. 请求体/查询串没过 zod 时 → **500** + Fastify 默认错误体，`message` 是
 *    **一串 JSON 字符串**（zod issue 数组）。`describeApiFailure()` 会把它
 *    解析成人话；不解析的话家长看到的就是一坨 JSON。这是后端的缺陷（应返回 400 +
 *    `{ok:false,error:{...}}`），但界面不能因此把 JSON 直接糊在脸上。
 *
 *    ⚠️ 2026-10-18 复核：根因是 `apps/api/src/app.ts` 里 `registerErrorHandler(app)`
 *    写在业务路由注册**之后**，已注册的路由仍绑着 Fastify 默认处理器。管理员登录
 *    空密码时也会走这条路（见 `./failure.ts` 顶部的完整链路）。文案归一化现在
 *    统一在 `./failure.ts`，本文件只是再导出。
 */
import type {
  ImportDuplicateItem,
  ListingPositionInfo,
  ListingReadingIssue,
  RepeatedPositionHint,
} from '@fht/shared';
import { toOptionalNumber } from './shape';
import { api } from './client';

// ---------------------------------------------------------------- 请求落点（scope）

/**
 * 字表请求该打到哪条路径上。
 *
 *   · `parent` —— `/students/:studentId/dictation/*`。孩子是**权限锚点**
 *     （"你是谁、你能看哪个孩子"），他**不**决定字表里有什么：
 *     换个孩子看到的是**同一份**字表。pīck 取词还会用到他的年级当默认值。
 *   · `admin` —— `/admin/dictation/*`。站点级，没有孩子，只有系统管理员能过门禁。
 *
 * ⚠️ 后端**刻意**不给 `resolveStudentAccess` 开"管理员也算有权限"的口子
 *   （那条函数是家庭数据的唯一防线），所以管理员**必须**走自己的路径。
 */
export type DictationScope =
  | { kind: 'parent'; studentId: string }
  | { kind: 'admin' };

/** `scope` → 路径前缀（**唯一**一处拼接，避免两条路径各写一份）。 */
export function dictationBase(scope: DictationScope): string {
  return scope.kind === 'admin' ? '/admin/dictation' : `/students/${scope.studentId}/dictation`;
}

// ---------------------------------------------------------------- 视图类型

/** 课本版本 —— 一本书 = **一个具体的册**（出版社 + 年级 + 学期）。 */
export interface TextbookView {
  id: string;
  publisher: string;
  /** 年级 code（`primary_1` …）—— 课本身份的一列。 */
  gradeCode: string;
  /** 学期（`上` / `下` / `不分`）—— 课本身份的另一列。 */
  semester: string;
  name: string;
  isDefault: boolean;
  /** 下面三个计数后端**总是**给；拿不到是 `null`（显示「—」，不是 0）。 */
  lessonCount: number | null;
  hanziCount: number | null;
  wordCount: number | null;
  /**
   * **这本书按「年级 + 学期 + 表」的收录数**（第五步）。
   *
   * 界面按"书 + 册 + 三张表"组织录入，靠它渲染
   * 「三年级上册 · 识字表 280 字 / 写字表 100 字 / 词语表 0 词」。
   * `null` = **这份数据没拿到**（老后端 / 字段名对不上）；
   * `[]` = 真的一份收录都还没有 —— 两者在界面上必须分开说。
   */
  gradeCounts: TextbookGradeCounts[] | null;
}

/** 一个课本里某一册的收录情况（`TextbookView.gradeCounts` 的元素）。 */
export interface TextbookGradeCounts {
  gradeCode: string;
  semester: string;
  /** 三个表各自的条数；键是表类型（`shizi` / `xiezi` / `cibiao`）。 */
  counts: Record<string, number>;
}

/** 一条收录：这个字在这本书的哪一册、哪张表上（`hanzi_listing` 一行）。 */
export interface HanziListingView {
  id: string;
  hanziId: string;
  textbookId: string;
  gradeCode: string;
  semester: string;
  /** `shizi`（识字表）/ `xiezi`（写字表）。 */
  listType: string;
  sortOrder: number;
  /**
   * **这个位置标定的读音**（迁移 0018；`null` = 没标，用字自己的默认读音）。
   *
   * 一行 = 一次录入的一个位置：同一个字出现两次、两个位置各读一个音时是两条。
   */
  reading?: string | null;
}

/** 字在词里的读音（`charReadings` 的元素）。 */
export interface WordCharReadingView {
  position: number;
  charText: string;
  /** 这个字**在这个词里**的读音（空串 = "还没填"，不是"没有读音"）。 */
  reading: string;
  /** true = 读的是**默认读音之外**的音（多音字在这个词里定音的证据）。 */
  nonDefault: boolean;
  /** 这个字有没有被收录进任何一张字表；`undefined` = 没拿到。 */
  listed?: boolean;
}

/** 一个字的读音汇总（`GET .../hanzi/:id/readings`）。 */
export interface HanziReadingsView {
  hanzi: HanziView;
  /**
   * **这个字被隐藏了哪几个读音**（迁移 0016；空数组 = 全部显示）。
   *
   * 这是**服务端**的值（`hanzi.hidden_readings`）—— 浮窗里「已隐藏的读音（N）」
   * 就按它切。家长端**只读**：他拿得到这一份（看得到管理员藏了哪些），
   * 但没有改它的入口（`PUT .../hidden-readings` 只在 `/admin/dictation/*` 上）。
   *
   * `null` = **这份数据没拿到**（老后端 / 字段名不符）—— 那时界面按"全部显示"
   * 渲染（`[]` 的语义），**不是**把某个音藏起来。
   */
  hiddenReadings: string[] | null;
  summary: {
    charText: string;
    readings: string[];
    primary: string;
    polyphonic: boolean;
    wordCountByReading: Record<string, number>;
    readingsWithoutWords: string[];
  };
  wordsByReading: {
    reading: string;
    primary: boolean;
    words: {
      id: string;
      wordText: string;
      position: number;
      pinyin: string;
      /** `false` = 自己造的词（可以改字）；`true` = 课本词语表（只读）。 */
      fromTextbook: boolean;
    }[];
  }[];
}

/** 课文。 */
export interface LessonView {
  id: string;
  textbookId: string;
  /** 后端列表里会给（`textbookName`）；单条创建/更新时不带 → `null`。 */
  textbookName: string | null;
  gradeCode: string;
  semester: string;
  lessonNo: number;
  lessonLabel: string;
  title: string;
  unitNo: number | null;
  sortOrder: number;
}

/** 生字。 */
export interface HanziView {
  id: string;
  textbookId: string;
  charText: string;
  /** 内置字表算出来的读音；未收录时是空串（`''` = 字表里没有这个字）。 */
  pinyinAuto: string;
  /** 实际生效的读音。 */
  pinyin: string;
  /** true = 家长改过（`PUT .../pinyin` 写过非空值）。 */
  pinyinOverridden: boolean;
  /** 多音字备选读音（已按"最常用在前"排序，可能与 `pinyinAuto` 相同）。 */
  pinyinAlternates: string[];
  /** 内置表 + 覆盖值的**全部读音**（多音字按音造词时列的选项）。 */
  readings: string[];
  /**
   * **教学口径的「多音字」** —— 后端 `hanzi.polyphonic`（**管理员人工标注**）。
   *
   * ⚠️ 它与 `readings.length >= 2` 不是一回事：那是技术层的"拼音表给了几个读音"
   *    （读音选择 / 按音造词 / 听写多音字专项用），这里是"这一册到底算不算多音字"。
   *    默认 `false` = 单音字；徽章与「只看多音字」筛选都只认它。
   */
  polyphonic: boolean;
  /**
   * **这个字被隐藏了哪几个读音**（`hanzi.hidden_readings`，迁移 0016）。
   *
   * 空数组 = **全部显示**（默认值，也是老数据的口径）。它是**显示口径**，
   * 与 `polyphonic`（教学口径的人工标注）是两件事；隐藏**不影响** `readings`、
   * 已有的词、听写取词与播放。
   *
   * ⚠️ 这里用 `string[]`（缺字段就 `[]`）而不是三态：**默认全显示**是安全的兜底
   *    （把"没拿到"当成"全显示"最多是多显示两个音，不会把一个还在用的音藏掉）。
   *    浮窗要说的"没拿到"由 `HanziReadingsView.hiddenReadings` 那一层负责。
   */
  hiddenReadings: string[];
  gradeCode: string | null;
  semester: string | null;
  /**
   * **这个字被收录在哪几张表上**（第五步）。**可能有 0 / 1 / 2 条以上** ——
   * 同一个字同时在识字表与写字表上时就是两条（这正是本轮改造要修的那件事）。
   *
   * `null` = **这份数据没拿到**；`[]` = 真的不属于任何一张表。两者必须分开说。
   */
  listings: HanziListingView[] | null;
  /**
   * **这一行代表哪一个位置**（迁移 0018 之后列表按"收录行"展开）。
   *
   * 同一个字录了 N 次 = N 行：`id` 相同、`listingId` 不同。
   * `null` = 这个字还没有任何收录（「全部表」里仍会出现）。
   */
  listingId?: string | null;
  /** 这个位置标定的读音（`null` = 没标，用 `pinyin`）。 */
  listingReading?: string | null;
  /** 这个位置的序号（`sort_order`）。 */
  listingSortOrder?: number | null;
  /**
   * **这个位置在录入原文里的行号**（迁移 0019；`null` = 没记过 / 手工新增）。
   *
   * 「浏览与编辑」在「恰好一张字表 + 每张卡都有行号」时按它分组（一行一块）。
   * 只要有一行是 `null` 就**整块不分组** —— 见 `components/dictation/browseLineGroups.ts`。
   * ⚠️ 它**不参与排序**：顺序仍是 `sort_order, created_at, id`。
   */
  listingLineNo?: number | null;
  note: string;
  /**
   * 关联课文。**列表响应里没有这个键**（后端只在 `POST` 的请求体里收它，
   * DTO 没往外带）→ 这里是 `null`，与"确实一节课都没关联"（`[]`）不同。
   * 编辑时必须据此判断"要不要先去查一次现有关联"，否则会把关联悄悄清空。
   */
  lessonIds: string[] | null;
}

/** 词语（含成语）。 */
export interface WordView {
  id: string;
  textbookId: string;
  wordText: string;
  pinyinAuto: string;
  pinyin: string;
  pinyinOverridden: boolean;
  category: string;
  /** 是否来自课本词语表。 */
  fromTextbook: boolean;
  gradeCode: string | null;
  semester: string | null;
  note: string;
  /**
   * 关联课文。**列表响应里没有这个键**（后端 DTO 没往外带）→ 这里是 `null`，
   * 与"确实一节课都没关联"（`[]`）不同。编辑时必须据此判断
   * "要不要先去查一次现有关联"，否则会把关联静默清空。
   */
  lessonIds: string[] | null;
  /**
   * **每个字在这个词里读哪个音**（第五步，按位置对齐 `chars`）。
   *
   * `null` = **没拿到**（列表接口不带它）；`[]` = 这个词没有可对齐的读音信息。
   */
  charReadings: WordCharReadingView[] | null;
}

/** 取词结果里的一条词（比列表多了来源课文与拆字）。 */
export interface PickedWordView {
  id: string;
  wordText: string;
  pinyin: string;
  pinyinAuto: string;
  pinyinOverridden: boolean;
  category: string;
  fromTextbook: boolean;
  gradeCode: string | null;
  semester: string | null;
  textbookId: string;
  /** 来源课文（可能多课）。 */
  lessons: { id: string; title: string; lessonNo: number; gradeCode: string; semester: string }[];
  chars: string[];
  /**
   * **每个字在这个词里的读音**（第五步）。"专门听写多音字"时要能看出
   * **为什么**这个词落进了这次听写（是哪个字读了非默认音）。
   * `null` = **没拿到**。
   */
  charReadings: WordCharReadingView[] | null;
  /**
   * **这个词在这个孩子的错词本里的情况**（第四步）。
   *
   *   · 对象 —— 在错词本里（附错了几次 / 下次该复习的时间）；
   *   · `null` —— **确实不在**错词本里；
   *   · `undefined` —— **字段没拿到**（老后端 / 响应形状变了）。
   *
   * 三种必须分得开：把"没拿到"显示成"没错过"会让家长以为这个词很安全。
   */
  mistake?:
    | { wrongCount: number; missingCount: number; nextReviewAt: string | null; due: boolean | null }
    | null;
}

/** 取词实际用到的条件（后端把"补全后的默认值"回显在这里）。 */
export interface PickAppliedView {
  textbookId: string | null;
  gradeCodes: string[];
  semesters: string[];
  lessonIds: string[];
  categories: string[];
  fromTextbookOnly: boolean | null;
  /** 这次按了哪几张表（识字表/写字表）；`[]` = 不限。 */
  listTypes: string[];
  /** 这次按了哪些读音；`[]` = 不限。 */
  readings: { char: string | null; reading: string }[];
  /** 是否只看多音字相关的词。 */
  polyphonicOnly: boolean;
  source: string;
  randomCount: number | null;
  seed: number | null;
}

/** 导入报告里的一行（created / updated / skipped 同形）。 */
export interface ImportRowView {
  /** 原文行号（1 开始）。 */
  line: number;
  /** 原文整行。 */
  raw: string;
  kind: string;
  text: string;
  /** `create` / `update` / `skip`。 */
  action: string;
  /** 已存在时指向库里那一行。 */
  id: string | null;
  reason: string | null;
  /**
   * **已存在的「字」**才有：库里的多音字标注（`hanzi.polyphonic`）。
   *
   * `null` = **没拿到**（新字 / 老后端没有这个字段）—— 与 `false`（确实还没标注）
   * 不同，但在这块提示里两者都按"还没标注"处理（宁可多提醒一句，也不要漏掉
   * 用户点名要看见的那个字；见 shared 的 `groupUnconfirmedPolyphonic`）。
   */
  polyphonic: boolean | null;
}

/** 导入报告里的一条问题（error / warning 同形）。 */
export interface ImportIssueView {
  /**
   * 原文行号；**0 = 整份文本级别的提示**（例如"文本里有 1 行重复，已自动合并"），
   * 不是"第 0 行"。界面必须分开渲染。
   */
  line: number;
  raw: string;
  message: string;
  /**
   * 出问题的那个**词元**（`天 天地` 里的 `天地`）。
   *
   * ⚠️ 三张表的准入规则是**逐个词元**判的（识字表/写字表只能单字、词语表只能 ≥2 字），
   *    所以错误必须说清是**哪一个**词元 —— 一行可能粘了 20 个字元。
   *    `null` = 这是整行级的问题（缺版次、课文行是空的……）。
   */
  token: string | null;
}

/**
 * 预览里那张**能核对的账**（2026-10-16 用户要求"数字要跟我录的对得上"）。
 *
 * ⚠️ 这些数字**由后端算一次**（`summarizeImportCounts`，纯函数、前后端共用），
 *    界面只负责渲染 —— 界面上另算一遍迟早与后端漂移，而这张账的意义正是"可信"。
 *    整块拿不到时是 `null`（界面要说"账没拿到"，**不是**显示一堆 0）。
 */
export interface ImportCountsView {
  tokens: number;
  recognized: number;
  problem: number;
  charRows: number;
  wordRows: number;
  created: number;
  updated: number;
  skipped: number;
  /** 同一份里重复、**只有一个读音**（= 打错了）被合并掉的条数。 */
  merged: number;
  /** 同一份里重复、但那个字是**多音字**（有 2+ 读音）→ 只存 1 条的条数（不算重复）。 */
  mergedPolyphonic: number;
  /** 多音字**去重后**的条数（"多音字有几个"）。 */
  polyphonicChars: number;
  /** **一共几个字/词（多音字只算一次）** —— 实际会留在字表里的行数口径。 */
  distinctUnits: number;
  unresolved: number;
  listType: string | null;
  /** 两条恒等式是否成立（`false` = 我们漏算/多算了，是 bug）。 */
  balanced: boolean;
}

/**
 * 预览里的一条**重复明细**（哪几行、哪个字/词、会怎么处理）。
 *
 * ⚠️ 形状**直接复用** `@fht/shared` 的 `ImportDuplicateItem`：后端就是用那个
 *    纯函数（`groupImportDuplicates` / `groupExistingDuplicates`）算出来的，
 *    这边只是解码 —— 两边各写一份类型迟早漂移，而这块信息的全部意义就是
 *    "说得清、对得上"。界面只渲染，**不许**自己再解析文本、再数一遍行号。
 */
export type ImportDuplicateItemView = ImportDuplicateItem;

/** 导入报告（预览与提交同形）。 */
export interface ImportReportView {
  parse: {
    header: string[];
    /** `tab` / `comma` / `spaces` / `pipe` / `ideographic`；拿不到是 `null`。 */
    delimiter: string | null;
    totalLines: number | null;
    blankLines: number | null;
    contextLines: number | null;
    charRows: number | null;
    wordRows: number | null;
    textbooks: string[];
    /** 用户在框里粘的**词元数**（"我录了 100 个字"里那个 100）。 */
    tokens: number | null;
  };
  summary: {
    rows: number | null;
    created: number | null;
    updated: number | null;
    skipped: number | null;
    errors: number | null;
    warnings: number | null;
    textbooksToCreate: number | null;
    lessonsToCreate: number | null;
    /** 那张**能核对的账**；`null` = 没拿到（不是"全是 0"）。 */
    counts: ImportCountsView | null;
  };
  created: ImportRowView[] | null;
  updated: ImportRowView[] | null;
  skipped: ImportRowView[] | null;
  /**
   * **重复明细**（用户原话："几行，什么字或词"）。
   *
   *   · `merged`   —— 同一份里重复、**只有一个读音**（打错了），会合并成 1 条；
   *   · `polyphonic` —— 同一份里重复、但那个字是**多音字**（有 2+ 读音）：
   *                    **不算重复**，单列一类，带 `readings`（读音清单）；
   *   · `existing` —— 之前已经录过（跳过 / 会更新）。
   *
   * `null` = **这次没拿到**（老后端没有这个字段）。那时界面要说"明细没拿到"，
   * 而不是把"没拿到"说成"没有重复" —— 两者在用户眼里是相反的两件事。
   */
  duplicates: {
    merged: ImportDuplicateItemView[];
    polyphonic: ImportDuplicateItemView[];
    existing: ImportDuplicateItemView[];
  } | null;
  errors: ImportIssueView[] | null;
  warnings: ImportIssueView[] | null;
  /** **只有提交**才有的关联写入数（预览时是 `{0,0,0}`）。 */
  links: {
    wordHanzi: number | null;
    wordLesson: number | null;
    hanziLesson: number | null;
    /** 这次写进 `hanzi_listing` 的**收录**条数（第五步；`null` = 老后端没给）。 */
    hanziListing: number | null;
  } | null;
  /**
   * **这次按「粘贴顺序」给字表编的号**（2026-10-19：逐字序号）。
   *
   * `null` = 老后端没给这个字段（那就别渲染这一行，而不是假装"重排了 0 条"）。
   * `reordered` 是已有收录被改成新序号的条数；`untouched > 0` = 这次只粘了一部分
   * （局部重贴 / 继续录）。
   */
  listingOrder: {
    scopes: number | null;
    orderable: number | null;
    reordered: number | null;
    untouched: number | null;
    fullTables: number | null;
  } | null;
  /** 因为 maxRows 被截断的行数（>0 要提示"分批导入"）。 */
  truncated: number | null;
  /**
   * **保存前的三道校验**（迁移 0018，2026-10-19）。
   *
   * `null` = 老后端没给这个字段 —— 界面**不能**假装"校验通过"。
   * `ok === false` 时「确认导入」必须禁用（后端提交也会 400）。
   */
  readings: {
    ok: boolean;
    positions: ListingPositionInfo[];
    missing: ListingReadingIssue[];
    /**
     * **文件里标了 `?`、用户还没确认**的位置（2026-10-20 第三道校验）。
     *
     * `ok === false` 时「确认导入」必须禁用。可选：老后端没这个字段 ⇒ `[]`
     * （界面**不能**因此把"需要确认"说成"没有"；但 `ok` 仍是权威判据）。
     */
    needsConfirm?: ListingReadingIssue[];
    conflicts: ListingReadingIssue[];
    missingChars: string[];
    missingSummary: string;
    /** 待确认的**字**（标题里那句「有 N 个字的读音需要你确认」）。 */
    needsConfirmChars?: string[];
    /** 「有 N 个字的读音需要你确认：得（第 3 行第 1 个字）…，确认之后才能保存。」 */
    needsConfirmSummary?: string;
    repeated: RepeatedPositionHint[];
  } | null;
  /**
   * 按「书 + 表」导入时回显的"这次录的是哪本书哪一册哪张表"。
   * `null` = 走的是通用导入（没有这个概念）。
   */
  list: {
    textbookId: string;
    textbookName: string;
    gradeCode: string;
    semester: string;
    listType: string;
    /**
     * 这本书是**这次保存时自动建出来的**（2026-10-18：三格没有对应书也能录）。
     * 老后端 / 通用导入没有这个字段 → `false`（不是"没建"，是"不知道 / 不适用"）。
     */
    textbookCreated: boolean;
  } | null;
}

/** 导入用的"默认课本/年级/学期"。 */
export interface ImportDefaults {
  /** 课本 id / 出版社代号 / 展示名都可以（后端会解析）。 */
  textbook?: string;
  gradeCode?: string;
  semester?: string;
}

// ---------------------------------------------------------------- 底层读取工具

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** 非空字符串；缺失或类型不对返回 `null`。 */
function toOptionalString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/** 必填字符串；缺失时回落成空串**并让调用方可见**（用 `MissingFields` 记录）。 */
function toStringOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function toBooleanOrFalse(value: unknown): boolean {
  return value === true;
}

/**
 * 把一个 ID 列表读成 `string[] | null`。
 *
 * `null` = **这个字段没拿到**（缺失或不是数组），与"空数组 = 确实一个都没有"
 * 是两件事。导入时用它来判"要不要补默认课本"就靠这个区分。
 */
function toStringArrayOrNull(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter((item): item is string => typeof item === 'string');
}

/** 对象数组的解码：键不是数组 → `null`；元素解码失败的条目**不静默丢弃**，而是记进 `missing`。 */
interface DecodedList<T> {
  items: T[] | null;
  missing: string[];
}

function decodeList<T>(
  source: unknown,
  keys: string[],
  decodeOne: (raw: Record<string, unknown>, missing: string[]) => T,
): DecodedList<T> {
  if (!isRecord(source)) return { items: null, missing: keys };
  let list: unknown;
  for (const key of keys) {
    if (Array.isArray(source[key])) {
      list = source[key];
      break;
    }
  }
  if (!Array.isArray(list)) return { items: null, missing: keys };
  const missing: string[] = [];
  const items = list.map((raw) => decodeOne(isRecord(raw) ? raw : {}, missing));
  return { items, missing };
}

/** 一个非空字符串数组字段；不是数组 → `[]`（用于 `pinyinAlternates` / `chars` 这类"没有就是没有"的字段）。 */
const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

// ---------------------------------------------------------------- 单条解码

/** 一条收录的解码。`listType` 为空串 = 后端给了个认不出来的值（原样显示）。 */
export function decodeHanziListing(raw: unknown): HanziListingView {
  const r = isRecord(raw) ? raw : {};
  return {
    id: toStringOrEmpty(r['id']),
    hanziId: toStringOrEmpty(r['hanziId']),
    textbookId: toStringOrEmpty(r['textbookId']),
    gradeCode: toStringOrEmpty(r['gradeCode']),
    semester: toStringOrEmpty(r['semester']),
    listType: toStringOrEmpty(r['listType']),
    sortOrder: toOptionalNumber(r['sortOrder']) ?? 0,
    reading: typeof r['reading'] === 'string' && r['reading'] !== '' ? r['reading'] : null,
  };
}

/**
 * 收录列表的解码：**键不是数组 → `null`（没拿到）**，不是 `[]`（一条都没有）。
 * 这条区分正是本文件开头那条铁律（"同一个字在不在两张表上"不能靠猜）。
 */
function decodeListingsOrNull(value: unknown): HanziListingView[] | null {
  if (!Array.isArray(value)) return null;
  return value.map(decodeHanziListing);
}

/** 逐位读音的解码：键不是数组 → `null`（没拿到）。 */
export function decodeCharReadings(raw: unknown): WordCharReadingView[] | null {
  if (!Array.isArray(raw)) return null;
  return raw.filter(isRecord).map((entry) => {
    const reading = toStringOrEmpty(entry['reading']);
    return {
      position: toOptionalNumber(entry['position']) ?? 0,
      charText: toStringOrEmpty(entry['charText']),
      reading,
      nonDefault: entry['nonDefault'] === true,
      /** `listed` 拿不到时**不写这个键**（三态：true / false / 没拿到）。 */
      ...(typeof entry['listed'] === 'boolean' ? { listed: entry['listed'] } : {}),
    };
  });
}

export function decodeTextbook(raw: unknown): TextbookView {
  const r = isRecord(raw) ? raw : {};
  const gradeCountsRaw = r['gradeCounts'];
  return {
    id: toStringOrEmpty(r['id']),
    publisher: toStringOrEmpty(r['publisher']),
    /**
     * 年级 / 学期是**课本身份**（一本书 = 一册），界面靠它们显示
     * 「人民教育出版社 · 小学一年级 · 上册」，并在选中书本后自动填好下面两格。
     */
    gradeCode: toStringOrEmpty(r['gradeCode']),
    semester: toStringOrEmpty(r['semester']),
    name: toStringOrEmpty(r['name']),
    isDefault: toBooleanOrFalse(r['isDefault']),
    lessonCount: toOptionalNumber(r['lessonCount']),
    hanziCount: toOptionalNumber(r['hanziCount']),
    wordCount: toOptionalNumber(r['wordCount']),
    /** ⚠️ 不是数组 = **没拿到**（`null`），不是"这本一册都没录"（`[]`）。 */
    gradeCounts: Array.isArray(gradeCountsRaw)
      ? gradeCountsRaw.filter(isRecord).map((entry) => {
          const countsRaw = isRecord(entry['counts']) ? entry['counts'] : {};
          const counts: Record<string, number> = {};
          for (const [key, value] of Object.entries(countsRaw)) {
            const parsed = toOptionalNumber(value);
            if (parsed !== null) counts[key] = parsed;
          }
          return {
            gradeCode: toStringOrEmpty(entry['gradeCode']),
            semester: toStringOrEmpty(entry['semester']),
            counts,
          };
        })
      : null,
  };
}

export function decodeLesson(raw: unknown): LessonView {
  const r = isRecord(raw) ? raw : {};
  return {
    id: toStringOrEmpty(r['id']),
    textbookId: toStringOrEmpty(r['textbookId']),
    textbookName: toOptionalString(r['textbookName']),
    gradeCode: toStringOrEmpty(r['gradeCode']),
    semester: toStringOrEmpty(r['semester']),
    lessonNo: toOptionalNumber(r['lessonNo']) ?? 0,
    lessonLabel: toStringOrEmpty(r['lessonLabel']),
    title: toStringOrEmpty(r['title']),
    unitNo: toOptionalNumber(r['unitNo']),
    sortOrder: toOptionalNumber(r['sortOrder']) ?? 0,
  };
}

export function decodeHanzi(raw: unknown): HanziView {
  const r = isRecord(raw) ? raw : {};
  const readings = stringList(r['readings']);
  return {
    id: toStringOrEmpty(r['id']),
    textbookId: toStringOrEmpty(r['textbookId']),
    charText: toStringOrEmpty(r['charText']),
    pinyinAuto: toStringOrEmpty(r['pinyinAuto']),
    pinyin: toStringOrEmpty(r['pinyin']),
    pinyinOverridden: toBooleanOrFalse(r['pinyinOverridden']),
    pinyinAlternates: stringList(r['pinyinAlternates']),
    /** 老后端没有 `readings` 时回落到"主读音 + 备选读音"，不让界面空着。 */
    readings:
      readings.length > 0
        ? readings
        : [toStringOrEmpty(r['pinyin']), ...stringList(r['pinyinAlternates'])].filter(
            (item) => item !== '',
          ),
    polyphonic: r['polyphonic'] === true,
    /**
     * 隐藏读音（迁移 0016）：`stringList` 在"字段缺失 / 不是数组"时给 `[]`，
     * 也就是**全部显示** —— 安全的兜底（不会把一个还在用的音藏掉）。
     */
    hiddenReadings: stringList(r['hiddenReadings']),
    gradeCode: toOptionalString(r['gradeCode']),
    semester: toOptionalString(r['semester']),
    listings: decodeListingsOrNull(r['listings']),
    /**
     * 这一行代表哪个位置（迁移 0018）：老后端没有这些字段 → `null`。
     * 同一个字出现 N 次时 `id` 相同、`listingId` 不同（界面 key 用它）。
     */
    listingId: toOptionalString(r['listingId']),
    listingReading: toOptionalString(r['listingReading']),
    listingSortOrder: toOptionalNumber(r['listingSortOrder']),
    /**
     * 原文行号（迁移 0019）：老后端没有这个字段 → `null`（界面据此不按行分组，
     * 与"这一行真的没有行号"同一种表现 —— 两者都**不该**分组）。
     */
    listingLineNo: toOptionalNumber(r['listingLineNo']),
    note: toStringOrEmpty(r['note']),
    lessonIds: toStringArrayOrNull(r['lessonIds']),
  };
}

export function decodeWord(raw: unknown): WordView {
  const r = isRecord(raw) ? raw : {};
  return {
    id: toStringOrEmpty(r['id']),
    textbookId: toStringOrEmpty(r['textbookId']),
    wordText: toStringOrEmpty(r['wordText']),
    pinyinAuto: toStringOrEmpty(r['pinyinAuto']),
    pinyin: toStringOrEmpty(r['pinyin']),
    pinyinOverridden: toBooleanOrFalse(r['pinyinOverridden']),
    category: toStringOrEmpty(r['category']),
    fromTextbook: toBooleanOrFalse(r['fromTextbook']),
    gradeCode: toOptionalString(r['gradeCode']),
    semester: toOptionalString(r['semester']),
    note: toStringOrEmpty(r['note']),
    /** 同 `HanziView.lessonIds`：列表响应里没有这个键。 */
    lessonIds: toStringArrayOrNull(r['lessonIds']),
    charReadings: decodeCharReadings(r['charReadings']),
  };
}

export function decodePickedWord(raw: unknown): PickedWordView {
  const r = isRecord(raw) ? raw : {};
  const lessonsRaw = Array.isArray(r['lessons']) ? r['lessons'] : [];
  return {
    id: toStringOrEmpty(r['id']),
    wordText: toStringOrEmpty(r['wordText']),
    pinyin: toStringOrEmpty(r['pinyin']),
    pinyinAuto: toStringOrEmpty(r['pinyinAuto']),
    pinyinOverridden: toBooleanOrFalse(r['pinyinOverridden']),
    category: toStringOrEmpty(r['category']),
    fromTextbook: toBooleanOrFalse(r['fromTextbook']),
    gradeCode: toOptionalString(r['gradeCode']),
    semester: toOptionalString(r['semester']),
    textbookId: toStringOrEmpty(r['textbookId']),
    lessons: lessonsRaw.filter(isRecord).map((l) => ({
      id: toStringOrEmpty(l['id']),
      title: toStringOrEmpty(l['title']),
      lessonNo: toOptionalNumber(l['lessonNo']) ?? 0,
      gradeCode: toStringOrEmpty(l['gradeCode']),
      semester: toStringOrEmpty(l['semester']),
    })),
    chars: stringList(r['chars']),
    /** 逐位读音（第五步新增）：`null` = **没拿到**，`[]` = 这个词没有可对齐的读音。 */
    charReadings: decodeCharReadings(r['charReadings']),
    /**
     * 错词信息。三种取值必须分得开：
     *   · 对象 —— 在错词本里；
     *   · `null` —— **确实不在**错词本里（第一批次里补位的新词就是 `null`）；
     *   · `undefined` —— **没拿到**（老后端 / 响应里没这个字段）。
     * 把 `undefined` 显示成"没错过"会让家长以为"这个词很安全"。
     */
    mistake: decodePickedWordMistake(r['mistake']),
  };
}

/** 取词结果里的一条词的错词信息（解码见上面的三态说明）。 */
function decodePickedWordMistake(
  raw: unknown,
): { wrongCount: number; missingCount: number; nextReviewAt: string | null; due: boolean | null } | null | undefined {
  if (raw === undefined) return undefined;
  if (raw === null) return null;
  if (!isRecord(raw)) return undefined;
  const wrongCount = toOptionalNumber(raw['wrongCount']);
  const missingCount = toOptionalNumber(raw['missingCount']);
  if (wrongCount === null || missingCount === null) return undefined;
  return {
    wrongCount,
    missingCount,
    nextReviewAt: toOptionalString(raw['nextReviewAt']),
    due: typeof raw['due'] === 'boolean' ? raw['due'] : null,
  };
}

/** 导入报告里的一行。 */
export function decodeImportRow(raw: unknown): ImportRowView {
  const r = isRecord(raw) ? raw : {};
  return {
    line: toOptionalNumber(r['line']) ?? 0,
    raw: toStringOrEmpty(r['raw']),
    kind: toStringOrEmpty(r['kind']),
    text: toStringOrEmpty(r['text']),
    action: toStringOrEmpty(r['action']),
    id: toOptionalString(r['id']),
    reason: toOptionalString(r['reason']),
    polyphonic: typeof r['polyphonic'] === 'boolean' ? r['polyphonic'] : null,
  };
}

/** 导入报告里的一条问题。 */
export function decodeImportIssue(raw: unknown): ImportIssueView {
  const r = isRecord(raw) ? raw : {};
  const message = toStringOrEmpty(r['message']);
  return {
    line: toOptionalNumber(r['line']) ?? 0,
    raw: toStringOrEmpty(r['raw']),
    // 拿不到 message 时**显式**说"后端没给原因"，而不是渲染一行空白
    message: message === '' ? '（后端没有给出原因）' : message,
    token: toOptionalString(r['token']),
  };
}

/**
 * 解那张账。
 *
 * ⚠️ **一个数字都不能少**：少任何一个就整块返回 `null`（界面说"账没拿到"）。
 *    给缺的项补 0 会让家长核对出一个**假的"对得上"** —— 那是这张账最不能出的错。
 */
export function decodeImportCounts(raw: unknown): ImportCountsView | null {
  if (!isRecord(raw)) return null;
  const keys = [
    'tokens',
    'recognized',
    'problem',
    'charRows',
    'wordRows',
    'created',
    'updated',
    'skipped',
    'merged',
    /**
     * ⚠️ 这三个是 2026-10-17 新增的两档（多音字 ≠ 打错）+ "一共几个字"的口径。
     *    老后端没有它们 ⇒ 整块按"账没拿到"处理（`null`），**不补 0**：
     *    补 0 会让"打错的重复"与"多音字的重复"看起来一样，而那正是这次要分开的事。
     */
    'mergedPolyphonic',
    'polyphonicChars',
    'distinctUnits',
    'unresolved',
  ] as const;
  const values: Record<string, number> = {};
  for (const key of keys) {
    const value = toOptionalNumber(raw[key]);
    if (value === null) return null;
    values[key] = value;
  }
  return {
    tokens: values['tokens']!,
    recognized: values['recognized']!,
    problem: values['problem']!,
    charRows: values['charRows']!,
    wordRows: values['wordRows']!,
    created: values['created']!,
    updated: values['updated']!,
    skipped: values['skipped']!,
    merged: values['merged']!,
    mergedPolyphonic: values['mergedPolyphonic']!,
    polyphonicChars: values['polyphonicChars']!,
    distinctUnits: values['distinctUnits']!,
    unresolved: values['unresolved']!,
    listType: toOptionalString(raw['listType']),
    /** 缺 `balanced` 时按 `false` 处理（"没验证过"不等于"对得上"）。 */
    balanced: raw['balanced'] === true,
  };
}

/**
 * 解码**一条重复明细**（`null` = 这一条形状不对，丢掉）。
 *
 * ⚠️ 宁可**丢掉一条形状不对的**，也不给它编一个行号 / 词元：这块信息的每一格
 *    都是用户用来去原文里核对的（"第 3 行那个「上」"），编出来的值比不显示更糟。
 */
function decodeImportDuplicateItem(raw: unknown): ImportDuplicateItemView | null {
  if (!isRecord(raw)) return null;
  const text = toStringOrEmpty(raw['text']);
  if (text === '') return null;
  if (!Array.isArray(raw['lines'])) return null;
  const lines: ImportDuplicateItemView['lines'] = [];
  for (const entry of raw['lines']) {
    if (!isRecord(entry)) continue;
    const line = toOptionalNumber(entry['line']);
    if (line === null) continue;
    const times = toOptionalNumber(entry['times']);
    lines.push({ line, times: times === null || times < 1 ? 1 : times });
  }
  if (lines.length === 0) return null;
  const action = raw['action'];
  const keptLine = toOptionalNumber(raw['keptLine']) ?? lines[0]!.line;
  const mergedCount = toOptionalNumber(raw['mergedCount']);
  const kind = raw['kind'] === 'word' ? 'word' : 'char';
  const listType = raw['listType'];
  /**
   * 多音字的读音清单（`['zhǎng', 'cháng']`）。
   * 拿不到时是空数组（界面不编读音；`action === 'polyphonic'` 时那句文案会说"读音没拿到"）。
   */
  const readings = Array.isArray(raw['readings'])
    ? raw['readings'].filter((entry): entry is string => typeof entry === 'string' && entry !== '')
    : [];
  return {
    kind,
    text,
    listType: listType === 'shizi' || listType === 'xiezi' || listType === 'cibiao' ? listType : null,
    scope: toOptionalString(raw['scope']),
    action:
      action === 'skip' || action === 'update' || action === 'polyphonic' ? action : 'merge',
    lines,
    keptLine,
    mergedCount: mergedCount === null || mergedCount < 0 ? 0 : mergedCount,
    reason: toOptionalString(raw['reason']),
    readings,
  };
}

/**
 * 解码报告里的 `duplicates`（三类）。
 *
 * `null` = **这份明细没拿到**（字段缺失 / 不是一个对象）。**不**兜成空数组：
 * `{merged: [], polyphonic: [], existing: []}` 会被界面渲染成"没有重复"，而真实
 * 情况可能是"后端没给" —— 本项目反复踩的就是这个坑。
 *
 * ⚠️ `polyphonic` 缺失时按**空数组**处理（而不是整块 `null`）：老后端会把多音字的
 *    重复也放进 `merged` 里，那些条目仍然会显示出来（只是没有单独分组）。
 *    整块判 `null` 反而会让"明细明明拿到了却说没拿到"。
 */
export function decodeImportDuplicates(raw: unknown): {
  merged: ImportDuplicateItemView[];
  polyphonic: ImportDuplicateItemView[];
  existing: ImportDuplicateItemView[];
} | null {
  if (!isRecord(raw)) return null;
  const decode = (source: unknown): ImportDuplicateItemView[] | null => {
    if (!Array.isArray(source)) return null;
    const out: ImportDuplicateItemView[] = [];
    for (const entry of source) {
      const item = decodeImportDuplicateItem(entry);
      if (item !== null) out.push(item);
    }
    return out;
  };
  const merged = decode(raw['merged']);
  const existing = decode(raw['existing']);
  if (merged === null || existing === null) return null;
  const polyphonic = raw['polyphonic'] === undefined ? [] : decode(raw['polyphonic']);
  if (polyphonic === null) return null;
  return { merged, polyphonic, existing };
}

/**
 * **保存前校验**（`report.readings`，迁移 0018）的解码。
 *
 * `null` = 没拿到（老后端）—— 界面必须说"这道校验没拿到"，**不能**当作通过。
 * 元素按 shared 的类型逐字段读；读不出来的给安全默认（空串 / 0 / false），
 * 因为这里只用于**展示**，真正的判定在服务层（提交时还会再拦一次）。
 */
export function decodeImportReadings(raw: unknown): ImportReportView['readings'] {
  if (!isRecord(raw)) return null;
  const positionsRaw = raw['positions'];
  if (!Array.isArray(positionsRaw)) return null;
  const decodePosition = (value: unknown): ListingPositionInfo => {
    const item = isRecord(value) ? value : {};
    const rawListType = item['listType'];
    return {
      charText: toStringOrEmpty(item['charText']),
      line: toOptionalNumber(item['line']) ?? 0,
      indexInLine: toOptionalNumber(item['indexInLine']) ?? 0,
      label: toStringOrEmpty(item['label']),
      /**
       * 这个位置在哪张字表上（预览按行分组时用来区分「识字表 · 第 3 行」与
       * 「写字表 · 第 3 行」）。认不出来 / 老后端没给 ⇒ `null`（标题只说行号）。
       */
      listType: rawListType === 'shizi' || rawListType === 'xiezi' ? rawListType : null,
      reading: toStringOrEmpty(item['reading']),
      effectiveReading: toStringOrEmpty(item['effectiveReading']),
      occurrenceCount: toOptionalNumber(item['occurrenceCount']) ?? 0,
      duplicate: item['duplicate'] === true,
      existing: item['existing'] === true,
      needsReading: item['needsReading'] === true,
      /** 文件里标了 `?`、还没确认（黄卡）；老后端没给 ⇒ false。 */
      uncertain: item['uncertain'] === true,
      /** 文件里标过 `?`（确认之后也仍然 true ⇒ 卡片画绿色）。 */
      wasUncertain: item['wasUncertain'] === true,
    };
  };
  const decodeIssue = (value: unknown): ListingReadingIssue => {
    const item = isRecord(value) ? value : {};
    return {
      kind:
        item['kind'] === 'duplicate-reading'
          ? 'duplicate-reading'
          : item['kind'] === 'needs-confirmation'
            ? 'needs-confirmation'
            : 'missing-reading',
      charText: toStringOrEmpty(item['charText']),
      line: toOptionalNumber(item['line']) ?? 0,
      indexInLine: toOptionalNumber(item['indexInLine']) ?? 0,
      label: toStringOrEmpty(item['label']),
      reading: toStringOrEmpty(item['reading']),
      alsoLabel: toStringOrEmpty(item['alsoLabel']),
      message: toStringOrEmpty(item['message']),
    };
  };
  const issueList = (value: unknown): ListingReadingIssue[] =>
    Array.isArray(value) ? value.map(decodeIssue) : [];
  const repeatedRaw = raw['repeated'];
  return {
    ok: raw['ok'] === true,
    positions: positionsRaw.map(decodePosition),
    missing: issueList(raw['missing']),
    needsConfirm: issueList(raw['needsConfirm']),
    conflicts: issueList(raw['conflicts']),
    missingChars: stringList(raw['missingChars']),
    missingSummary: toStringOrEmpty(raw['missingSummary']),
    needsConfirmChars: stringList(raw['needsConfirmChars']),
    needsConfirmSummary: toStringOrEmpty(raw['needsConfirmSummary']),
    repeated: Array.isArray(repeatedRaw)
      ? repeatedRaw.filter(isRecord).map((item) => ({
          charText: toStringOrEmpty(item['charText']),
          first: decodePosition(item['first']),
          others: Array.isArray(item['others']) ? item['others'].map(decodePosition) : [],
          message: toStringOrEmpty(item['message']),
        }))
      : [],
  };
}

export function decodeImportReport(raw: unknown): ImportReportView {
  const r = isRecord(raw) ? raw : {};
  const parse = isRecord(r['parse']) ? r['parse'] : {};
  const summary = isRecord(r['summary']) ? r['summary'] : {};
  const links = isRecord(r['links']) ? r['links'] : null;
  const listingOrder = isRecord(r['listingOrder']) ? r['listingOrder'] : null;

  const rows = (source: unknown): ImportRowView[] | null =>
    Array.isArray(source) ? source.map(decodeImportRow) : null;
  const issues = (source: unknown): ImportIssueView[] | null =>
    Array.isArray(source) ? source.map(decodeImportIssue) : null;

  return {
    parse: {
      header: stringList(parse['header']),
      delimiter: toOptionalString(parse['delimiter']),
      totalLines: toOptionalNumber(parse['totalLines']),
      blankLines: toOptionalNumber(parse['blankLines']),
      contextLines: toOptionalNumber(parse['contextLines']),
      charRows: toOptionalNumber(parse['charRows']),
      wordRows: toOptionalNumber(parse['wordRows']),
      textbooks: stringList(parse['textbooks']),
      /** 用户在框里粘的**词元数**（"我录了 100 个字"里那个 100）。 */
      tokens: toOptionalNumber(parse['tokens']),
    },
    summary: {
      rows: toOptionalNumber(summary['rows']),
      created: toOptionalNumber(summary['created']),
      updated: toOptionalNumber(summary['updated']),
      skipped: toOptionalNumber(summary['skipped']),
      errors: toOptionalNumber(summary['errors']),
      warnings: toOptionalNumber(summary['warnings']),
      textbooksToCreate: toOptionalNumber(summary['textbooksToCreate']),
      lessonsToCreate: toOptionalNumber(summary['lessonsToCreate']),
      /** 那张**能核对的账**；拿不到是 `null`（界面说"账没拿到"，不显示 0）。 */
      counts: decodeImportCounts(summary['counts']),
    },
    created: rows(r['created']),
    updated: rows(r['updated']),
    skipped: rows(r['skipped']),
    /** 重复明细（哪几行、哪个字/词、会怎么处理）；`null` = 这次没拿到。 */
    duplicates: decodeImportDuplicates(r['duplicates']),
    errors: issues(r['errors']),
    warnings: issues(r['warnings']),
    links:
      links === null
        ? null
        : {
            wordHanzi: toOptionalNumber(links['wordHanzi']),
            wordLesson: toOptionalNumber(links['wordLesson']),
            hanziLesson: toOptionalNumber(links['hanziLesson']),
            /** 这次写进 `hanzi_listing` 的**收录**条数（第五步）。 */
            hanziListing: toOptionalNumber(links['hanziListing']),
          },
    /** 这次按「粘贴顺序」编的号；老后端没有这个键 ⇒ `null`（界面不渲染那一行）。 */
    listingOrder:
      listingOrder === null
        ? null
        : {
            scopes: toOptionalNumber(listingOrder['scopes']),
            orderable: toOptionalNumber(listingOrder['orderable']),
            reordered: toOptionalNumber(listingOrder['reordered']),
            untouched: toOptionalNumber(listingOrder['untouched']),
            fullTables: toOptionalNumber(listingOrder['fullTables']),
          },
    truncated: toOptionalNumber(r['truncated']),
    /** **保存前的三道校验**（迁移 0018）；`null` = 没拿到（不是"通过"）。 */
    readings: decodeImportReadings(r['readings']),
    /**
     * 按「书 + 表」导入时后端回显的"这次录的是哪本书哪一册哪张表"。
     * `null` = 走的是通用导入（没有这个概念）。
     */
    list:
      isRecord(r['list'])
        ? {
            textbookId: toStringOrEmpty(r['list']['textbookId']),
            textbookName: toStringOrEmpty(r['list']['textbookName']),
            gradeCode: toStringOrEmpty(r['list']['gradeCode']),
            semester: toStringOrEmpty(r['list']['semester']),
            listType: toStringOrEmpty(r['list']['listType']),
            /** 只有"这次顺手建出来的"才是 true；没有这个字段 = false。 */
            textbookCreated: r['list']['textbookCreated'] === true,
          }
        : null,
  };
}

// ---------------------------------------------------------------- 错误体的统一出口

/**
 * 失败响应的归一化（zod 的 JSON message、`details` 的四种形状、兜底文案）
 * **全部**在 `./failure.ts` —— 2026-10-18 起它是全站共用出口：
 * 登录页 / 动态口令页显式走 `describeFailureMessage()`，其余页面经
 * `toErrorMessage()` 一起受益（后者也改为走这里）。
 *
 * 本文件保留**同名再导出**，是为了不动既有的 import：
 * `import { describeApiFailure } from './dictation'`、`dictation.describeApiFailure(…)`
 * 以及 `__tests__/dictation*.test.ts` 都继续可用。
 */
export {
  DEFAULT_FAILURE_MESSAGE,
  describeApiFailure,
  describeFailureMessage,
  parseZodMessage,
  readFailureDetails,
} from './failure';
export type { ApiFailure, DictationFailure, FailureDetail } from './failure';

// ---------------------------------------------------------------- 端点

/**
 * 课本列表（`GET {scope}/textbooks`）。
 *
 * ⚠️ 只读：新建/改课本是**维护类**，只有系统管理员能做（见 `dictationAdmin.ts`）。
 */
export async function listTextbooks(scope: DictationScope): Promise<{
  textbooks: TextbookView[] | null;
  missing: string[];
}> {
  const data = await api.get<unknown>(`${dictationBase(scope)}/textbooks`);
  const decoded = decodeList(data, ['textbooks'], (raw) => decodeTextbook(raw));
  return { textbooks: decoded.items, missing: decoded.missing };
}

/** 课文列表（`{lessons, total}`）。 */
export async function listLessons(
  scope: DictationScope,
  query: Record<string, string | number | boolean | undefined> = {},
): Promise<{ lessons: LessonView[] | null; total: number | null; missing: string[] }> {
  const data = await api.get<unknown>(`${dictationBase(scope)}/lessons`, query);
  const decoded = decodeList(data, ['lessons'], (raw) => decodeLesson(raw));
  const total = isRecord(data) ? toOptionalNumber(data['total']) : null;
  return { lessons: decoded.items, total, missing: decoded.missing };
}

/** 生字列表（`{hanzi, total}`）。 */
export async function listHanzi(
  scope: DictationScope,
  query: Record<string, string | number | boolean | undefined> = {},
): Promise<{ hanzi: HanziView[] | null; total: number | null; missing: string[] }> {
  const data = await api.get<unknown>(`${dictationBase(scope)}/hanzi`, query);
  const decoded = decodeList(data, ['hanzi'], (raw) => decodeHanzi(raw));
  const total = isRecord(data) ? toOptionalNumber(data['total']) : null;
  return { hanzi: decoded.items, total, missing: decoded.missing };
}

/** 这个字能组哪些词（`{hanzi, words}`）。 */
export async function listWordsOfHanzi(
  scope: DictationScope,
  hanziId: string,
): Promise<{ hanzi: HanziView | null; words: WordView[] | null }> {
  const data = await api.get<unknown>(`${dictationBase(scope)}/hanzi/${hanziId}/words`);
  const record = isRecord(data) ? data : {};
  const words = Array.isArray(record['words']) ? record['words'].map(decodeWord) : null;
  return {
    hanzi: isRecord(record['hanzi']) ? decodeHanzi(record['hanzi']) : null,
    words,
  };
}

/** 词语列表（`{words, total}`）。 */
export async function listWords(
  scope: DictationScope,
  query: Record<string, string | number | boolean | undefined> = {},
): Promise<{ words: WordView[] | null; total: number | null; missing: string[] }> {
  const data = await api.get<unknown>(`${dictationBase(scope)}/words`, query);
  const decoded = decodeList(data, ['words'], (raw) => decodeWord(raw));
  const total = isRecord(data) ? toOptionalNumber(data['total']) : null;
  return { words: decoded.items, total, missing: decoded.missing };
}

/**
 * 一个字的**读音汇总**（"它在哪些词里读 cháng、在哪些词里读 zhǎng"）。
 *
 * 返回 `null` = **没查到**（接口出错 / 形状不符），界面要说出来而不是当成
 * "这个字没有读音"。
 */
export async function fetchHanziReadings(
  scope: DictationScope,
  hanziId: string,
): Promise<HanziReadingsView | null> {
  let data: unknown;
  try {
    data = await api.get<unknown>(`${dictationBase(scope)}/hanzi/${hanziId}/readings`);
  } catch {
    return null;
  }
  if (!isRecord(data) || !isRecord(data['hanzi']) || !isRecord(data['summary'])) return null;
  const summary = data['summary'];
  const countsRaw = isRecord(summary['wordCountByReading']) ? summary['wordCountByReading'] : {};
  const counts: Record<string, number> = {};
  for (const [key, value] of Object.entries(countsRaw)) {
    const parsed = toOptionalNumber(value);
    if (parsed !== null) counts[key] = parsed;
  }
  const wordsByReadingRaw = Array.isArray(data['wordsByReading']) ? data['wordsByReading'] : [];
  return {
    hanzi: decodeHanzi(data['hanzi']),
    /**
     * 隐藏读音：**三态**保留（`null` = 这份数据没拿到）。
     *
     * 为什么这里不像 `HanziView` 那样兜成 `[]`：这个端点就是浮窗的唯一数据源，
     * 而"藏了哪几个"是浮窗里要**如实**显示的东西 —— 拿不到时按"全显示"渲染
     * 已经是最安全的视觉结果，但调用方仍应能知道"这一份没拿到"
     * （老后端 / 字段改名），而不是以为"管理员一个都没藏"。
     */
    hiddenReadings: Array.isArray(data['hiddenReadings'])
      ? stringList(data['hiddenReadings'])
      : null,
    summary: {
      charText: toStringOrEmpty(summary['charText']),
      readings: stringList(summary['readings']),
      primary: toStringOrEmpty(summary['primary']),
      polyphonic: summary['polyphonic'] === true,
      wordCountByReading: counts,
      readingsWithoutWords: stringList(summary['readingsWithoutWords']),
    },
    wordsByReading: wordsByReadingRaw.filter(isRecord).map((entry) => ({
      reading: toStringOrEmpty(entry['reading']),
      primary: entry['primary'] === true,
      words: (Array.isArray(entry['words']) ? entry['words'] : []).filter(isRecord).map((word) => ({
        id: toStringOrEmpty(word['id']),
        wordText: toStringOrEmpty(word['wordText']),
        position: toOptionalNumber(word['position']) ?? 0,
        pinyin: toStringOrEmpty(word['pinyin']),
        /** `false` = 自己造的词（可以改字）；`true` = 课本词语表（只读）。 */
        fromTextbook: word['fromTextbook'] === true,
      })),
    })),
  };
}

// ---------------------------------------------------------------- 收录（识字表 / 写字表）

/**
 * 一本书某一册某一张表里的内容。
 *
 * `listType` 是 `shizi` / `xiezi` 时 `hanzi` 有内容、`words` 为空；
 * 是 `cibiao` 时反过来。
 */
export interface ListItemsResult {
  listType: string;
  hanzi: HanziView[] | null;
  words: WordView[] | null;
  total: number | null;
}

export async function fetchListItems(
  scope: DictationScope,
  query: {
    textbookId: string;
    gradeCode: string;
    semester: string;
    listType: string;
    keyword?: string;
    limit?: number;
    offset?: number;
  },
): Promise<ListItemsResult> {
  const data = await api.get<unknown>(`${dictationBase(scope)}/lists/items`, {
    textbookId: query.textbookId,
    gradeCode: query.gradeCode,
    semester: query.semester,
    listType: query.listType,
    ...(query.keyword !== undefined && query.keyword !== '' ? { keyword: query.keyword } : {}),
    limit: query.limit ?? 200,
    ...(query.offset !== undefined ? { offset: query.offset } : {}),
  });
  const record = isRecord(data) ? data : {};
  return {
    listType: toStringOrEmpty(record['listType']) || query.listType,
    hanzi: Array.isArray(record['hanzi']) ? record['hanzi'].map(decodeHanzi) : null,
    words: Array.isArray(record['words']) ? record['words'].map(decodeWord) : null,
    total: toOptionalNumber(record['total']),
  };
}

// ---------------------------------------------------------------- 取词

/** 取词请求体（全部可选）。 */
export interface PickPayload {
  textbookId?: string;
  gradeCodes?: string[];
  semesters?: string[];
  lessonIds?: string[];
  categories?: string[];
  fromTextbookOnly?: boolean;
  /**
   * **按「表」筛词**（`shizi` 识字表 / `xiezi` 写字表）—— 第五步新增。
   *
   * 语义是 **ANY**：词里有**任何一个字**被收录进选中的表就算。
   * 界面上就是"这次按哪张表听写"那一排按钮。
   */
  listTypes?: string[];
  /**
   * **只听某个读音的词** —— 第五步新增。
   * `{ char: '长', reading: 'cháng' }` = 只取"长"读 cháng 的词。
   */
  readings?: { char?: string; reading: string }[];
  /** **只看多音字相关的词**（有字读了默认读音之外的音）。 */
  isPolyphonic?: boolean;
  randomCount?: number;
  limit?: number;
  /**
   * 取词来源：
   *   · `all` —— 全部词；
   *   · `wrong_first` —— **错词优先**（错词排前面，剩余名额补新词）；
   *   · `wrong` —— **只取错词**。
   *
   * ⚠️ 涉及错词时后端**必须**能拿到孩子（`dictation_mistake` 有 `student_id`），
   *   所以只有挂在 `/students/:id/...` 下面的调用点能用它 —— 取词接口的路径里
   *   本来就带了那个 id，前端不需要额外传。
   */
  source?: 'all' | 'wrong_first' | 'wrong';
  seed?: number;
  includePinyin?: boolean;
}

/**
 * 听写取词（**只读预览**，一行都不写；"布置听写"用的是同一个后端函数）。
 *
 * ⚠️ 它**只对家长可用**：`source` 涉及错词时后端要按 `studentId` 查错词本
 *   （`dictation_mistake` 有 `student_id`），而 `/admin/dictation/*` 是站点级、
 *   没有孩子。管理员要"预览会取到哪些词"这件事**没有产品意义** ——
 *   他维护的是字表，不是给谁布置听写。
 */
export async function pickWords(
  scope: DictationScope,
  payload: PickPayload,
): Promise<{
  applied: PickAppliedView | null;
  words: PickedWordView[] | null;
  total: number | null;
  /**
   * 这次用到的错词总数（**只有 `source` 涉及错词时后端才给**）。
   * `null` = 没拿到 / 这个来源没有这个概念。
   */
  wrongTotal: number | null;
  note: string | null;
  missing: string[];
}> {
  if (scope.kind !== 'parent') {
    // 编译期已经挡住；这里是运行期兜底（`any` 绕过类型时也不会静默发错路径）
    throw new Error('取词只对家长可用（它需要按孩子查错词本）');
  }
  const data = await api.post<unknown>(`${dictationBase(scope)}/words/pick`, payload);
  const decoded = decodeList(data, ['words'], (raw) => decodePickedWord(raw));
  const record = isRecord(data) ? data : {};
  const appliedRaw = isRecord(record['applied']) ? record['applied'] : null;

  return {
    applied:
      appliedRaw === null
        ? null
        : {
            textbookId: toOptionalString(appliedRaw['textbookId']),
            gradeCodes: stringList(appliedRaw['gradeCodes']),
            semesters: stringList(appliedRaw['semesters']),
            lessonIds: stringList(appliedRaw['lessonIds']),
            categories: stringList(appliedRaw['categories']),
            fromTextbookOnly:
              typeof appliedRaw['fromTextbookOnly'] === 'boolean'
                ? appliedRaw['fromTextbookOnly']
                : null,
            listTypes: stringList(appliedRaw['listTypes']),
            readings: (Array.isArray(appliedRaw['readings']) ? appliedRaw['readings'] : [])
              .filter(isRecord)
              .map((entry) => ({
                char: toOptionalString(entry['char']),
                reading: toStringOrEmpty(entry['reading']),
              })),
            polyphonicOnly: appliedRaw['polyphonicOnly'] === true,
            source: toStringOrEmpty(appliedRaw['source']),
            randomCount: toOptionalNumber(appliedRaw['randomCount']),
            seed: toOptionalNumber(appliedRaw['seed']),
          },
    words: decoded.items,
    total: toOptionalNumber(record['total']),
    wrongTotal: toOptionalNumber(record['wrongTotal']),
    note: toOptionalString(record['note']),
    missing: decoded.missing,
  };
}

// ---------------------------------------------------------------- 纯展示辅助（可测）

/**
 * 表类型 → 中文（识字表 / 写字表 / 词语表）—— 第五步取代了旧的
 * "1类字 / 2类字"（`hanzi.category` 那一列已被迁移 0009 删掉）。
 *
 * ⚠️ 与 `@fht/shared` 的 `LIST_TYPE_LABELS` 同口径；这里**再写一份**是因为
 *    前端解码层要能在"后端给了个认不出来的值"时原样显示它（不静默成空）。
 */
export const LIST_TYPE_LABEL: Record<string, string> = {
  shizi: '识字表（会认）',
  xiezi: '写字表（会写）',
  cibiao: '词语表',
};

/** 表类型的短名字（徽章里用）。 */
export const LIST_TYPE_SHORT_LABEL: Record<string, string> = {
  shizi: '识字表',
  xiezi: '写字表',
  cibiao: '词语表',
};

/** 词语类别 → 中文。 */
export const WORD_CATEGORY_LABEL: Record<string, string> = {
  common: '常用词',
  phrase: '词语',
  idiom: '成语',
};

/** 学期 → 中文。 */
export const SEMESTER_LABEL: Record<string, string> = {
  上: '上册',
  下: '下册',
  不分: '不分学期',
};

/**
 * 取词来源 → 中文（认不出来的原样显示，不静默成空）。
 *
 * 第四步新增了 `wrong_first` / `wrong` 两个取值；老值 `all` 保持原样。
 */
export const PICK_SOURCE_LABEL: Record<string, string> = {
  all: '全部词语',
  wrong_first: '错词优先',
  wrong: '只听写错词',
};

export function sourceLabel(source: string): string {
  return source === '' ? '—' : (PICK_SOURCE_LABEL[source] ?? source);
}

/** 表类型标签；认不出来的原样显示（不静默成空）。 */
export function listTypeLabel(listType: string | null | undefined): string {
  if (listType === null || listType === undefined || listType === '') return '—';
  return LIST_TYPE_LABEL[listType] ?? listType;
}

export function listTypeShortLabel(listType: string | null | undefined): string {
  if (listType === null || listType === undefined || listType === '') return '—';
  return LIST_TYPE_SHORT_LABEL[listType] ?? listType;
}

/**
 * 一个字的收录 → 一句人话（"三年级上册 · 识字表"）。
 *
 * ⚠️ 收录可能有 0 / 1 / 2 条以上；`null` = **没拿到这份数据**，
 * 界面必须分开说（本项目反复踩过的那条线）。
 */
export function describeListing(listing: HanziListingView, gradeLabelOf: (code: string) => string): string {
  return `${gradeLabelOf(listing.gradeCode)}${semesterLabel(listing.semester)} · ${listTypeShortLabel(
    listing.listType,
  )}`;
}

export function wordCategoryLabel(category: string): string {
  return category === '' ? '—' : (WORD_CATEGORY_LABEL[category] ?? category);
}

export function semesterLabel(semester: string | null): string {
  if (semester === null || semester === '') return '—';
  return SEMESTER_LABEL[semester] ?? semester;
}

/**
 * 逐位读音 → 一句人话（"长 读 cháng（不是默认音）"）。
 *
 * 空读音**要说出来**（"待填"）—— 它是"这个字在这个词里读什么还不知道"，
 * 不是"没有读音"。
 */
export function describeCharReadings(readings: readonly WordCharReadingView[] | null): string {
  if (readings === null) return '—';
  if (readings.length === 0) return '';
  return readings
    .map((entry) => `${entry.charText} ${entry.reading === '' ? '（读音待填）' : entry.reading}${entry.nonDefault ? '（多音字）' : ''}`)
    .join(' · ');
}

/**
 * 计数的显示口径：**真 0 显示 0，拿不到显示「—」**。
 *
 * 这条函数存在只为一件事：把"0 / 拿不到"的区别写在一处，界面各处不再各写 `?? 0`。
 */
export function countLabel(value: number | null | undefined, suffix = ''): string {
  if (value === null || value === undefined) return '—';
  return `${value}${suffix}`;
}

/**
 * 列表的"空 vs 拿不到"判定（**这是本项目三次踩坑的那条线**）。
 *
 * @param list  解码后的列表（`null` = 字段没拿到）
 * @param total 后端给的 `total`（`null` = 没拿到 / 这个端点没有 total）
 */
export function describeListState(
  list: readonly unknown[] | null | undefined,
  total: number | null,
): { state: 'unknown' | 'empty' | 'mismatch' | 'ok'; message: string } {
  if (list === null || list === undefined) {
    return {
      state: 'unknown',
      message: '没拿到这份数据（后端返回的字段名与预期不一致）—— 这不是"一条都没有"。',
    };
  }
  if (list.length === 0) {
    if (total !== null && total > 0) {
      return {
        state: 'mismatch',
        message: `后端说一共有 ${total} 条，却返回了 0 条 —— 数据对不上，请刷新重试（不要当成"没有数据"）。`,
      };
    }
    return { state: 'empty', message: '' };
  }
  if (total !== null && total > list.length) {
    return {
      state: 'ok',
      /**
       * ⚠️ 2026-10-20：列表**一次取回、全部渲染**（分页已删）。所以这里说的是
       *    "**这次取到**多少"，不是"当前渲染多少" —— 取到多少就显示多少。
       *    ⛔ 不要再写"可以继续加载"：能不能看全取决于服务端这次给了多少条
       *    （与 `DICTATION_BROWSE_LIMIT_MAX` = 20000 的上限有关；列表末句总数
       *    会说明"服务器这次只返回了 N 条"）。
       */
      message: `一共 ${total} 条，这次只取到 ${list.length} 条（缩小筛选范围可以看全）。`,
    };
  }
  return { state: 'ok', message: '' };
}

